import React from 'react';
import styled, { keyframes } from 'styled-components';

// Keyframe animations
const float1 = keyframes`
  0%, 100% {
    transform: translate(0, 0) rotate(0deg);
    opacity: 0.4;
  }
  50% {
    transform: translate(30px, -40px) rotate(180deg);
    opacity: 0.6;
  }
`;

const float2 = keyframes`
  0%, 100% {
    transform: translate(0, 0) rotate(0deg);
    opacity: 0.3;
  }
  50% {
    transform: translate(-40px, 30px) rotate(-180deg);
    opacity: 0.5;
  }
`;

const float3 = keyframes`
  0%, 100% {
    transform: translate(0, 0) rotate(0deg);
    opacity: 0.35;
  }
  50% {
    transform: translate(20px, 50px) rotate(90deg);
    opacity: 0.55;
  }
`;

const gridPulse = keyframes`
  0%, 100% {
    opacity: 0.4;
  }
  50% {
    opacity: 0.6;
  }
`;

const BackgroundContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  z-index: 0;
  background: linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 50%, #0f0f1a 100%);
`;

const GridOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image:
    linear-gradient(rgba(141, 215, 247, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(141, 215, 247, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  animation: ${gridPulse} 8s ease-in-out infinite;
`;

const RadialGlow = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 800px;
  height: 800px;
  background: radial-gradient(
    circle,
    rgba(141, 215, 247, 0.08) 0%,
    rgba(141, 215, 247, 0.03) 30%,
    transparent 70%
  );
  pointer-events: none;
`;

const FloatingShape = styled.div`
  position: absolute;
  border: 1px solid rgba(141, 215, 247, 0.15);
  background: rgba(141, 215, 247, 0.03);
  pointer-events: none;
  will-change: transform, opacity;

  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
    opacity: 0.3;
  }
`;

const Hexagon1 = styled(FloatingShape)`
  top: 15%;
  left: 10%;
  width: 80px;
  height: 80px;
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  animation: ${float1} 20s ease-in-out infinite;
`;

const Hexagon2 = styled(FloatingShape)`
  top: 70%;
  right: 15%;
  width: 60px;
  height: 60px;
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  animation: ${float2} 25s ease-in-out infinite;
`;

const Circle1 = styled(FloatingShape)`
  top: 25%;
  right: 20%;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  animation: ${float3} 18s ease-in-out infinite;
`;

const Diamond = styled(FloatingShape)`
  bottom: 20%;
  left: 20%;
  width: 50px;
  height: 50px;
  transform: rotate(45deg);
  animation: ${float2} 22s ease-in-out infinite;
`;

/**
 * AnimatedBackground - Futuristic animated background for login page
 *
 * Features:
 * - Deep space gradient background
 * - Subtle animated grid overlay
 * - 3-4 floating geometric shapes
 * - Central radial glow
 * - Performance optimized with will-change
 * - Respects reduced-motion preference
 */
const AnimatedBackground = () => {
  return (
    <BackgroundContainer>
      <GridOverlay />
      <RadialGlow />
      <Hexagon1 />
      <Hexagon2 />
      <Circle1 />
      <Diamond />
    </BackgroundContainer>
  );
};

export default AnimatedBackground;
